import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import type { UpsellSurface } from 'fym-shared/src/music24/watermarkUpsell';
import { useTRPCAPI, useTRPCMutation } from '../../../../../trpc/trpc';
import { BillingInterval, Music24Plan, music24CheckoutPlanInput } from '../../../config/music24StripePlans';
import { useUser } from '../../../../users/hooks/useUser';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { getUpgradeSessionErrorMessage } from '../../../utils/getUpgradeSessionErrorMessage';
import { useMyTeamRole } from '../../../../team/useMyTeamRole';
import { clearCheckoutIntent } from '../../../config/checkoutIntent';
import { useIsNativeEmbed } from '../../../navigation/hooks/useIsNativeEmbed';
import { hasTrackingConsent } from '../../../../analytics/consent/trackingConsent';
import { rememberUpsellCheckoutSurface } from '../../../watermarkUpsell/upsellCheckoutSurface';
import { useDisplayCurrency } from '../../../config/useDisplayCurrency';

function getSuccessUrl(): string {
  return `${window.location.origin}/app/paymentComplete?session_id={CHECKOUT_SESSION_ID}`;
}

function getCancelUrl(): string {
  return window.location.href;
}

export interface Music24Checkout {
  isLoggedIn: boolean;
  loadingPlanId: string | null;
  /**
   * Starts the checkout of a plan for one artist. A plan checkout sends no extra artists: they are
   * sold only in Settings -> Subscription (owner, 2026-10-23). On a plan change the server moves the
   * extra artists the workspace already pays for to the new plan.
   */
  subscribe: (plan: Music24Plan, interval: BillingInterval, options?: Music24CheckoutOptions) => Promise<void>;
}

export interface Music24CheckoutOptions {
  /**
   * The upsell surface that starts the checkout (the takeover of design trial-upsell b3). It goes
   * into the Stripe Checkout session metadata and into `purchase_completed`.
   */
  upsellSurface?: UpsellSurface;
}

/** Music24 no longer offers a Stripe trial — the 7-day trial is granted at signup, before any
 * card is on file, and drops to Free on its own. There is nothing left to offer or check here. */
export function useMusic24Checkout(): Music24Checkout {
  const intl = useIntl();
  const trpcAPI = useTRPCAPI();
  const { data: userData } = useUser();
  const { showError } = useShowToast();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  // Ruling 8: only the workspace owner manages billing. Every plan card already disables its
  // Subscribe/Switch button for a member; this is the last line of defense against anything else
  // reaching `subscribe` directly.
  const isMember = useMyTeamRole() === 'MEMBER';
  // App Store 3.1.1 / Play billing: a native app's web view never starts a Stripe checkout.
  const isNativeEmbed = useIsNativeEmbed();
  // Checkout charges the currency of the prices on screen (decision D7).
  const currency = useDisplayCurrency();

  const { mutateAsync: createCheckoutSession } = useTRPCMutation(
    trpcAPI.billing.createMusic24CheckoutSession.mutationOptions()
  );
  const { mutateAsync: createUpgradeSession } = useTRPCMutation(
    trpcAPI.billing.createMusic24UpgradeSession.mutationOptions()
  );

  const isLoggedIn = Boolean(userData?.user);

  const subscribe = useCallback(
    async (plan: Music24Plan, interval: BillingInterval, options?: Music24CheckoutOptions): Promise<void> => {
      if (isMember || isNativeEmbed) {
        return;
      }
      const upsellSurface = options?.upsellSurface;
      if (upsellSurface !== undefined) {
        rememberUpsellCheckoutSurface(upsellSurface);
      }
      // A checkout is starting — any leftover intent from a website "Choose <plan>" button has
      // done its job, so it must not reopen the plan window on the next page (ruling C2).
      clearCheckoutIntent();
      // The server finds the current Stripe price of the tier and interval: the web holds no price id.
      const planInput = music24CheckoutPlanInput(plan, interval);
      setLoadingPlanId(plan.id);

      try {
        // A logged-in visitor resolves through the upgrade endpoint, which knows whether they
        // already subscribe and lands them in Stripe already authenticated when they do. Only a
        // signed-out (marketing) visitor goes straight to Checkout.
        // The portal's return_url isn't templated like Checkout's success_url — it must be a
        // plain URL the customer lands back on after confirming the plan change.
        const result = isLoggedIn
          ? await createUpgradeSession({
              ...planInput,
              returnUrl: getCancelUrl(),
              trackingConsent: hasTrackingConsent(),
              currency,
              ...(upsellSurface === undefined ? {} : { upsellSurface }),
            })
          : await createCheckoutSession({
              ...planInput,
              successUrl: getSuccessUrl(),
              cancelUrl: getCancelUrl(),
              customerEmail: userData?.user?.email,
            });

        if (result.url) {
          window.location.href = result.url;
        }
      } catch (error) {
        // The raw error can carry internal Stripe text — log it, but never show it to the
        // customer. Only a small, translated set of messages reaches the toast.
        console.error('Failed to create checkout session', error);
        showError(getUpgradeSessionErrorMessage(error, intl));
      } finally {
        setLoadingPlanId(null);
      }
    },
    [
      isLoggedIn,
      createUpgradeSession,
      createCheckoutSession,
      userData?.user?.email,
      showError,
      intl,
      isMember,
      isNativeEmbed,
      currency,
    ]
  );

  return {
    isLoggedIn,
    loadingPlanId,
    subscribe,
  };
}
